<script setup lang="ts">
import type { EdgeStyles } from '~/types/storyblok/shared'

interface Props {
  edgeType?: EdgeStyles
  backgroundColor?: string
  imageAltText?: string
  fontColor?: string
  media?: string
  link: {
    label: string
    href?: string
  }
  heading: string
  text: string
  additionalText?: string
}

const props = withDefaults(defineProps<Props>(), {
  edgeType: 'wave',
  backgroundColor: 'var(--white)',
  fontColor: 'var(--black)',
  media: '',
  imageAltText: '',
  additionalText: '',
})

const hasEdge = computed(() => !!props.edgeType)
</script>

<template>
  <section class="collection-hero" :class="[{ 'has-image': media }]" data-test="collection-hero">
    <div class="text-content" data-test="collection-hero-content">
      <span v-if="hasEdge" class="edge" :class="[edgeType]" />
      <NuxtLink v-if="link.href && link.label" :to="$url(link.href)" class="breadcrumb">
        {{
          link.label
        }}
      </NuxtLink>
      <span v-else-if="link.label" class="breadcrumb">{{ link.label }}</span>
      <h1 class="heading">
        {{ heading }}
      </h1>
      <p class="text">
        {{ text }}
      </p>
      <p v-if="additionalText" class="additional-text">
        {{ additionalText }}
      </p>
    </div>
    <div v-if="media" class="media" data-test="collection-hero-media">
      <ImageResponsive provider="storyblok" :src="media" :alt="imageAltText" :lazy="false" />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/rules/edges';
@import 'assets/scss/typography/headings';
@import 'assets/scss/typography/body';

section {
  display: flex;
  position: relative;
  flex-direction: column-reverse;
  background-color: v-bind(backgroundColor);
}

.collection-hero {
  padding-top: 4rem;

  .text-content {
    padding-top: 6rem;
  }
  
  .media {
    display: none;
  }

  &.has-image {
    @media (min-width: $large-breakpoint){
      padding-top: unset;

      .text-content {
        padding-top: unset;
      }

      .media {
        display: block;
      }
    }
  }
}

.text-content {
  padding: var(--spacing-xl) 1.6rem;
  width: 100%;
  position: relative;
  background: inherit;
  color: v-bind(fontColor);

  .heading {
    @include h5;
    color: inherit;

    @media (min-width: $small-breakpoint) {
      @include h4;
      color: inherit;
    }
  }

  .breadcrumb {
    @include body2;
    color: inherit;
    opacity: 0.6;
  }

  .text {
    @include body1;
    max-width: 50rem;
    margin-top: 1.4rem;
    color: inherit;
  }

  .additional-text {
    @include caption1;
    max-width: 50rem;
    margin-top: 1.4rem;
    color: inherit;
  }
}

.edge {
  position: absolute;
  top: -1.5rem;
  left: 0;
  width: 100%;
  height: 1.5rem;
  background: inherit;
  border: none;
}

.edge.teeth {
  @include teeth;
  z-index: 1;
}

.wave {
  @include wave;
}
.scallop {
  @include scallop;
}

.media {
  width: 100%;

  @media (min-width: $small-breakpoint) {
    width: 50%;
  }
}

@media (min-width: $small-breakpoint) {
  section {
    flex-direction: row;
    align-items: flex-end;
  }

  .text-content {
    width: calc(50% - 1px);
    padding: var(--spacing-xl) var(--spacing-m);

    .edge {
      display: none;
    }
  }

  .media {
    width: calc(50% + 1px);
  }
}
</style>
